import { Braze } from '~/app/braze/braze'
import { sendGA4FCustomEvent } from '~/app/ga4f'
import {
  IBrazeClickCartIntroLog,
  IBrazeClickOrderIntro,
  IBrazeCompositeOrderComplete,
  IBrazePurchaseEventLog,
} from '~/app/braze/brazeCustomLog'
import { toOrderCompleteBrazePurchaseDomestic } from '~/app/components/order-converter/converter/DomesticOrderConverter'
import { toOrderCompleteBrazePurchaseLeisure } from '~/app/components/order-converter/converter/LeisureOrderConverter'
import { toOrderCompleteBrazePurchaseTrain } from '~/app/components/order-converter/converter/TrainOrderConverter'
import { toOrderCompleteBrazePurchaseBus } from '~/app/components/order-converter/converter/BusOrderConverter'
import { OrderCompleteV3Query } from '~/app/query/order-complete/order-complete.graphql'
import { CartItemsV3Query } from '~/__generated__/app/query/Cart/Cart.graphql'
import { CriteoTracker, CriteoOneTagEvent } from '@yanolja-rnd/ya-tracker'
import { flatten } from 'ramda'
import { isTruthy } from '~/app/utils'
import { IGlobalOrderCompleteClientLogs } from '~/api/converters/global/order-complete/ClientLogs/index.type'
import { IPurchaseEventParams } from '~/app/ga/gaLogInterface'
import { IFbqPurchaseData, trackFbq } from '~/app/pixel'
import { CartProductOptions } from './kinesis/protobuf/module/yanolja_clientlog_custom_order/cart-product-options'
import { CartPageLog } from './kinesis/protobuf/clientlogs/order-misc-common-txtpb'
import { sendRtbEvent } from '~/app/rtb-tracker'

export interface ISendPurchaseEventParams {
  orderDetails: OrderCompleteV3Query['orderDetailsV3']
  paidPrice: number
  isFromCart: boolean
  isCartSelectAll?: boolean
}

export const toBrazePurchaseData = ({
  order,
  payments,
  paidPrice,
}): IBrazePurchaseEventLog | null =>
  order.__typename === 'OrderDetailsDomestic'
    ? toOrderCompleteBrazePurchaseDomestic(order, payments, paidPrice)
    : order.__typename === 'OrderDetailsLeisureProduct'
      ? toOrderCompleteBrazePurchaseLeisure(order.items, payments, paidPrice)
      : order.__typename === 'OrderDetailsTrain'
        ? toOrderCompleteBrazePurchaseTrain(order, payments, paidPrice)
        : order.__typename === 'OrderDetailsBus'
          ? toOrderCompleteBrazePurchaseBus(order, payments, paidPrice)
          : null

export const sendClickCartIntroEvent = (data: IBrazeClickCartIntroLog) => {
  Braze.sendCustomEvent('click_cart_Intro', data)
  sendGA4FCustomEvent('click_cart_Intro', data)
}

export const sendClickOrderIntroEvent = (data: IBrazeClickOrderIntro) => {
  Braze.sendCustomEvent('click_order_Intro', data)
  sendGA4FCustomEvent('click_order_Intro', data)
}

export const sendCompositeOrderCompleteEvent = (data: IBrazeCompositeOrderComplete) => {
  Braze.sendCustomEvent('composition_order_complete', data)
  sendGA4FCustomEvent('composite_order_complete', data)
}

export const sendBrazePurchaseEvent = (data: IBrazePurchaseEventLog | null) => {
  Braze.sendPurchaseEvent(data)
}

export const sendCriteoCartEvent = (cartItems: CartItemsV3Query['cartItemsV3']) => {
  const items = cartItems
    .map(item => {
      if (item.__typename === 'DomesticAccommodationCartItem') {
        return item.options.map(option => ({
          id: `P-${item.propertyId}`,
          price: option.price,
          quantity: 1,
        }))
      }
      if (item.__typename === 'DomesticLeisureCartItem') {
        return item.options.map(option => ({
          id: `LE-${item.dealId}`,
          price: option.price * option.quantity,
          quantity: option.quantity,
        }))
      }
      if (item.__typename === 'DomesticTrainCartItem') {
        return item.options.map(option => {
          const [departure, arrival] = option.trains
          const data = [
            {
              id: `${departure.departureStationCode}_${departure.arrivalStationCode}`,
              price: departure.price,
              quantity: 1,
            },
          ]
          if (arrival) {
            data.push({
              id: `${arrival.departureStationCode}_${arrival.arrivalStationCode}`,
              price: arrival.price,
              quantity: 1,
            })
          }
          return data
        })
      }
      if (item.__typename === 'DomesticBusCartItem') {
        return item.options.map(option => {
          const [departure, arrival] = option.reservations
          const departurePrice = departure.seats.reduce((total, seat) => total + seat.price, 0)
          const data = [
            {
              id: `${departure.departureTerminalNo}_${departure.arrivalTerminalNo}`,
              price: departurePrice,
              quantity: 1,
            },
          ]
          if (arrival) {
            const arrivalPrice = arrival.seats.reduce((total, seat) => total + seat.price, 0)
            data.push({
              id: `${arrival.departureTerminalNo}_${arrival.arrivalTerminalNo}`,
              price: arrivalPrice,
              quantity: 1,
            })
          }
          return data
        })
      }
      return null
    })
    .filter(isTruthy)

  const data: CriteoOneTagEvent[] = [
    {
      event: 'setEmail',
      email: '',
      hash_method: '',
    },
    {
      event: 'setZipcode',
      zipcode: '',
    },
    {
      event: 'viewBasket',
      item: flatten(items),
    },
  ]

  if (process.env.NODE_ENV === 'development') {
    console.table(data)
    return
  }

  CriteoTracker.initialize()
  CriteoTracker.push(data)
}

export const getCriteoOrderCompleteData = (
  id: string,
  categoryOrders: OrderCompleteV3Query['orderDetailsV3']['categoryOrders']
): CriteoOneTagEvent[] => {
  const items = categoryOrders
    .map(order => {
      if (order.__typename === 'OrderDetailsDomestic') {
        return {
          id: `P-${order.propertyId}`,
          price: order.price,
          quantity: 1,
        }
      }
      if (order.__typename === 'OrderDetailsLeisureProduct') {
        return {
          id: `LE-${order.items[0].dealId}`,
          price: order.items.reduce((acc, item) => acc + item.price, 0),
          quantity: order.items.length,
        }
      }
      if (order.__typename === 'OrderDetailsTrain') {
        const [departure, arrival] = order.tickets
        const data = [
          {
            id: `${departure.departureStationCode}_${departure.arrivalStationCode}`,
            price: departure.price,
            quantity: 1,
          },
        ]
        if (arrival) {
          data.push({
            id: `${arrival.departureStationCode}_${arrival.arrivalStationCode}`,
            price: arrival.price,
            quantity: 1,
          })
        }
        return data
      }
      if (order.__typename === 'OrderDetailsBus') {
        const [departure, arrival] = order.reservations
        const departurePrice = departure.tickets
          .flat()
          .reduce((total, ticket) => total + ticket.price, 0)
        const data = [
          {
            id: `${departure.departureTerminalNo}_${departure.arrivalTerminalNo}`,
            price: departurePrice,
            quantity: 1,
          },
        ]
        if (arrival) {
          const arrivalPrice = arrival.tickets
            .flat()
            .reduce((total, ticket) => total + ticket.price, 0)
          data.push({
            id: `${arrival.departureTerminalNo}_${arrival.arrivalTerminalNo}`,
            price: arrivalPrice,
            quantity: 1,
          })
        }
        return data
      }
      return null
    })
    .filter(isTruthy)

  return [
    {
      event: 'setEmail',
      email: '',
      hash_method: '',
    },
    {
      event: 'setZipcode',
      zipcode: '',
    },
    {
      event: 'trackTransaction',
      id,
      item: flatten(items),
    },
  ]
}

export const sendCriteoEvent = (data: CriteoOneTagEvent[]) => {
  if (process.env.NODE_ENV === 'development') {
    console.table(data)
    return
  }

  CriteoTracker.initialize()
  CriteoTracker.push(data)
}

export const sendGlobalPurchaseGALog = (
  logData: IPurchaseEventParams & { hitCallback?: () => void }
) => {
  sendGA4FCustomEvent('purchase', logData)
}

const sendGlobalCompositeOrderCompleteBrazeLog = (
  logData: IGlobalOrderCompleteClientLogs['braze']
) => {
  Braze.sendCustomEvent('composite_order_complete', logData)
}

export const sendGlobalPurchaseBrazeLog = (
  logData: IGlobalOrderCompleteClientLogs['brazePurchase']
) => {
  Braze.sendPurchaseEvent(logData)
}

export const sendGlobalOrderCompleteBrazeLog = (
  logData: IGlobalOrderCompleteClientLogs['braze']
) => {
  sendGlobalCompositeOrderCompleteBrazeLog(logData)
}

export const sendGlobalPurchasePixelLog = (logData: IFbqPurchaseData) => {
  trackFbq('Purchase', logData)
}

export const sendCartPageViewEvent = ({
  braze,
  ga,
  kinesis,
  criteo,
  rtb,
}: {
  braze: {
    cart_id: string
    cart_category: string
    cart_category_cnt: number
    cart_amount: number
  }
  ga: {
    cart_id: string
    cart_category: string
    cart_category_cnt: number
    cart_amount: number
  }
  kinesis: CartProductOptions[]
  criteo: { id: string; price: number; quantity: number }[]
  rtb: {
    hashedMemberNo: string
    productIds: string[]
  }
}) => {
  CartPageLog.CartViewInfo.view({ productOptions: kinesis })

  Braze.sendCustomEvent('click_cart_Intro', braze)
  sendGA4FCustomEvent('click_cart_Intro', ga)

  CriteoTracker.initialize()
  CriteoTracker.push([
    { event: 'setEmail', email: '', hash_method: '' },
    { event: 'setZipcode', zipcode: '' },
    { event: 'viewBasket', item: criteo },
  ])

  sendRtbEvent(
    {
      eventType: 'basketstatus',
      offerIds: `${rtb.productIds.join(',')}`,
    },
    { eventType: 'uid', id: `${rtb.hashedMemberNo}` }
  )
}
